import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

// Module-level state for single in-flight refresh across concurrent requests
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  return next(attachAuthHeaders(req, auth)).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only attempt refresh on 401, and never on the refresh endpoint itself
      if (error.status === 401 && !isRefreshEndpoint(req.url)) {
        return handleRefresh(req, next, auth);
      }
      return throwError(() => error);
    })
  );
};

function isRefreshEndpoint(url: string): boolean {
  return url.includes('/auth/refresh-token') ||
         url.includes('/auth/login') ||
         url.includes('/auth/mfa/verify') ||
         url.includes('/auth/device/verify');
}

function attachAuthHeaders(req: HttpRequest<unknown>, auth: AuthService): HttpRequest<unknown> {
  const token      = auth.getToken();
  const tenantCode = auth.user()?.tenantCode;
  const headers: Record<string, string> = {};
  if (token)      headers['Authorization'] = `Bearer ${token}`;
  if (tenantCode) headers['X-Tenant-Code'] = tenantCode;
  return Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;
}

function handleRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject.next(null);

    return auth.refreshToken().pipe(
      switchMap(res => {
        isRefreshing = false;
        const newToken = res.data?.accessToken ?? '';
        refreshSubject.next(newToken);
        // Retry original request with refreshed credentials (token + tenant code)
        return next(attachAuthHeaders(req, auth));
      }),
      catchError(err => {
        isRefreshing = false;
        refreshSubject.next(null);
        auth.clearSession();
        return throwError(() => err);
      })
    );
  }

  // Another request already triggered a refresh — wait for it then retry
  return refreshSubject.pipe(
    filter((t): t is string => t !== null),
    take(1),
    switchMap(() => next(attachAuthHeaders(req, auth)))
  );
}
